import { AgGridColumnProps } from "ag-grid-react";
import UploadExplanationRealEstate from "../../components/__haim/ImportLeads/UploadExplanationRealEstate";
import { validateRealEstateColumnValues } from "./../ImportLeads/realEstateImportLeadsValidations";
import { ICrmSpecifications } from "./../../@types/crmSpecifications";
import {
  NumberComparator,
  currencyFormatter,
  StringFilterParams,
  NumberFilterParams
} from "./../../utils/Haim/agGridCellRenderers";
import {
  callerFirstNameCol,
  callerLastNameCol,
  contactOriginCol,
  contactOriginAdditionalInfoCol,
  createdTimeCol,
  updatedTimeCol,
  lastBulkMessageReceivedTimeCol,
  optedOutOfBulkMessagesCol,
  isInSpamDatabaseCol,
  additionalNotesCol,
  tagsCol
} from "./GenericCrmSpecifications";
import { metadataParsedCallerPhoneNumberCol } from "./GenericCrmSpecifications";
import { CONTACT_IMPORT_COLUMN_NAMES } from "./contactImportColumnNames";
import {
  crmStatusPossibleValues,
  customerTypePossibleValues
} from "./contactImportColumnNames";
import { getListingAddressString } from "utils/Haim/utils";
import { textIncludes } from "utils/table";
import { floors, crmStatusLabels } from "../../@types/sharedSchema";

export class RealEstateCrmSpecifications implements ICrmSpecifications {
  getDefaultDataColumns(): AgGridColumnProps[] {
    return realEstateColumns;
  }

  getLeadsImportUISection() {
    return <UploadExplanationRealEstate />;
  }

  runLeadsImportValidations(parsedRow: any, rawRow: any) {
    return validateRealEstateColumnValues(parsedRow, rawRow);
  }

  getLeadsImportDemoFileName() {
    return "/static/_haim/demoImportFileRealEstate.xlsx";
  }
}

// Re-exported from `contactImportColumnNames`, which is where they live now so the import
// validations can read them without loading this file's React tree. Kept exported here for
// the call sites that already import them from the specs.
export {
  customerTypePossibleValues,
  crmStatusPossibleValues,
  optedOutOfBulkMessagesPossibleValues
} from "./contactImportColumnNames";

export const customerBudgetCol: AgGridColumnProps = {
  colId: CONTACT_IMPORT_COLUMN_NAMES.customerBudget.field,
  field: CONTACT_IMPORT_COLUMN_NAMES.customerBudget.field,
  filter: "BudgetFilter",
  minWidth: 150,
  editable: true,
  headerName: CONTACT_IMPORT_COLUMN_NAMES.customerBudget.header,
  comparator: NumberComparator,
  valueFormatter: currencyFormatter
};

export const interestedInLocationsCol: AgGridColumnProps = {
  colId: "interestedInLocations",
  field: "interestedInLocations",
  filter: true,
  minWidth: 300,
  headerName: "מעוניין באזורים",
  cellRenderer: "InterestedInLocationsRenderer",
  valueGetter: ({ data: { interestedInLocations } }) => {
    return JSON.stringify(interestedInLocations);
  },
  filterParams: {
    buttons: ["clear"],
    filterOptions: ["contains"],
    suppressAndOrCondition: true,
    textCustomComparator: (_filterOption: string, columnValue: string, filterText: string) => {
      const splittedFilterValue = filterText.trim().split(" ").filter(Boolean);

      if (!splittedFilterValue.length) return true;

      const interestedInLocations = JSON.parse(columnValue);

      if (!interestedInLocations?.length) return false;

      const parsedColumnValue = interestedInLocations.map(getListingAddressString).join(", ");

      let isMatch = false;

      for (const filterValue of splittedFilterValue) {
        isMatch = textIncludes(parsedColumnValue, filterValue);

        if (isMatch) break;
      }

      return isMatch;
    }
  }
};

export const interestedInPropertyTypesCol: AgGridColumnProps = {
  colId: "interestedInPropertyTypes",
  field: "interestedInPropertyTypes",
  filter: true,
  minWidth: 300,
  headerName: "מעוניין בסוגי נכסים",
  cellRenderer: "InterestedInPropertyTypesRenderer",
  valueGetter: ({ data: { interestedInPropertyTypes } }) => {
    return JSON.stringify(interestedInPropertyTypes);
  },
  filterParams: {
    buttons: ["clear"],
    filterOptions: ["contains"],
    suppressAndOrCondition: true,
    textCustomComparator: (_filterOption: string, columnValue: string, filterText: string) => {
      const splittedFilterValue = filterText.trim().split(" ").filter(Boolean);

      if (!splittedFilterValue.length) return true;

      const interestedInPropertyTypes = JSON.parse(columnValue);

      if (!interestedInPropertyTypes?.length) return false;

      const parsedColumnValue = interestedInPropertyTypes.join(", ");

      let isMatch = false;

      for (const filterValue of splittedFilterValue) {
        isMatch = textIncludes(parsedColumnValue, filterValue);

        if (isMatch) break;
      }

      return isMatch;
    }
  }
};

// ── `neighborhood` and `street` COLUMNS ARE GONE (2026-09-01) ────────────────────
//
// They were free-text mirrors of `interestedInLocations`, superseded by it years ago:
// `interestedInLocations` holds `{ city, neighborhood, street }` per area, which is the
// shape every reader in the product actually uses — the matcher, the listing alerts, the
// canonical location service. The two flat strings held a comma-joined smear of the same
// thing with no city attached, and nothing reconciled the two representations.
//
// The server has refused to write them for a while (`contactWriteRules`: "deprecated
// free-text mirrors … nothing derives them, so accepting them would let two
// representations of the same fact drift with no reconciler"), so the import grid was the
// last place they were shown, and editable at that. Removing the columns also removes the
// `שכונה`/`רחוב` header mappings, so a third-party file carrying those columns now has
// them DROPPED rather than written onto the contact — which is the same rule every other
// unmapped column follows.

export const roomsMinCol: AgGridColumnProps = {
  colId: CONTACT_IMPORT_COLUMN_NAMES.roomsMin.field,
  field: CONTACT_IMPORT_COLUMN_NAMES.roomsMin.field,
  headerName: CONTACT_IMPORT_COLUMN_NAMES.roomsMin.header,
  cellEditor: "agSelectCellEditor",
  cellEditorParams: {
    values: [""].concat(
      [...Array(21).keys()].map((e, i) => (e / 2).toString() + (i === 20 ? "+" : ""))
    ) // 0 - 10+ rooms
  },
  filter: "agNumberColumnFilter",
  filterParams: NumberFilterParams,
  editable: true
};

export const roomsMaxCol: AgGridColumnProps = {
  colId: CONTACT_IMPORT_COLUMN_NAMES.roomsMax.field,
  field: CONTACT_IMPORT_COLUMN_NAMES.roomsMax.field,
  headerName: CONTACT_IMPORT_COLUMN_NAMES.roomsMax.header,
  cellEditor: "agSelectCellEditor",
  cellEditorParams: {
    values: [""].concat(
      [...Array(21).keys()].map((e, i) => (e / 2).toString() + (i === 20 ? "+" : "")).slice(1)
    ) // 0.5 - 10+ rooms
  },
  filter: "agNumberColumnFilter",
  filterParams: NumberFilterParams,
  editable: true
};

export const floorsMinCol: AgGridColumnProps = {
  colId: "floorsMin",
  field: "floorsMin",
  headerName: "מקומה",
  cellEditor: "agSelectCellEditor",
  cellEditorParams: {
    values: [""].concat(floors.map(String)) // -4 - 50 floors
  },
  filter: "agNumberColumnFilter",
  filterParams: NumberFilterParams,
  editable: true
};

export const floorsMaxCol: AgGridColumnProps = {
  colId: "floorsMax",
  field: "floorsMax",
  headerName: "עד קומה",
  cellEditor: "agSelectCellEditor",
  cellEditorParams: {
    values: [""].concat(floors.map(String)) // -4 - 50 floors
  },
  filter: "agNumberColumnFilter",
  filterParams: NumberFilterParams,
  editable: true
};

export const customerTypeCol: AgGridColumnProps = {
  colId: CONTACT_IMPORT_COLUMN_NAMES.customerType.field,
  field: CONTACT_IMPORT_COLUMN_NAMES.customerType.field,
  headerName: CONTACT_IMPORT_COLUMN_NAMES.customerType.header,
  cellEditor: "agSelectCellEditor",
  cellEditorParams: {
    values: customerTypePossibleValues
  },
  cellRenderer: "ColoredLabelRenderer",
  cellRendererParams: {
    colors: {
      מוכר: "success",
      קונה: "warning",
      שוכר: "info",
      משכיר: "error"
    }
  },
  filter: true,
  filterParams: StringFilterParams,
  editable: true
};

// CRM v2 status column. Grid cells hold the Hebrew label (what realtors type in files);
// the upload flow converts label → CrmStatus id before writing the contact.
export const crmStatusCol: AgGridColumnProps = {
  colId: CONTACT_IMPORT_COLUMN_NAMES.crmStatus.field,
  field: CONTACT_IMPORT_COLUMN_NAMES.crmStatus.field,
  headerName: CONTACT_IMPORT_COLUMN_NAMES.crmStatus.header,
  cellEditor: "agSelectCellEditor",
  minWidth: 120,
  cellEditorParams: {
    values: crmStatusPossibleValues
  },
  cellRenderer: "ColoredLabelRenderer",
  cellRendererParams: {
    colors: {
      [crmStatusLabels.new]: "info",
      [crmStatusLabels.in_progress]: "primary",
      [crmStatusLabels.paused]: "warning",
      [crmStatusLabels.won]: "success"
    }
  },
  filter: true,
  filterParams: StringFilterParams,
  editable: true
};

export const realEstateColumns: AgGridColumnProps[] = [
  metadataParsedCallerPhoneNumberCol,
  callerFirstNameCol,
  callerLastNameCol,
  customerTypeCol,
  customerBudgetCol,
  roomsMinCol,
  roomsMaxCol,
  floorsMinCol,
  floorsMaxCol,
  tagsCol,
  interestedInLocationsCol,
  interestedInPropertyTypesCol,
  crmStatusCol,
  contactOriginCol,
  contactOriginAdditionalInfoCol,
  additionalNotesCol,
  createdTimeCol,
  updatedTimeCol,
  lastBulkMessageReceivedTimeCol,
  optedOutOfBulkMessagesCol,
  isInSpamDatabaseCol
];
